import { ProfileService } from '../../pages/private/modules/profile/service/profile.service';
import { CompanyService } from '../../pages/private/modules/company/services/company-profile.service';
import { FeatureJobsService } from '../../pages/private/modules/dashboard/services/featurejobs.service';
import { FeatureJobApplicationsService } from '../../pages/private/modules/dashboard/services/featurejobapplications.service';
import { FeatureJob } from '../../pages/private/modules/dashboard/models/featurejobs.model';
import { FeatureJobApplications } from '../../pages/private/modules/dashboard/models/featurejobapplications.model';

export class PreloadService {
  private static getPhotoKey = (userId: string) => `profilePhoto_${userId}`;
  private static getCompanyLogoKey = (companyId: string) => `companyLogo_${companyId}`;
  private static getFeaturedJobsKey = () => `featuredJobs_preloaded`;
  private static getArchivedJobsKey = () => `archivedJobs_preloaded`;
  private static getJobApplicationsKey = () => `jobApplications_preloaded`;

  private static getPhotoFromLocalStorage = (userId: string): string | null =>
    localStorage.getItem(this.getPhotoKey(userId));

  private static getLogoFromLocalStorage = (companyId: string): string | null =>
    localStorage.getItem(this.getCompanyLogoKey(companyId));

  private static storePhotoInLocalStorage = (userId: string, dataUrl: string) => {
    localStorage.setItem(this.getPhotoKey(userId), dataUrl);
  };

  private static storeLogoInLocalStorage = (companyId: string, dataUrl: string) => {
    localStorage.setItem(this.getCompanyLogoKey(companyId), dataUrl);
  };

  private static storeFeaturedJobsInLocalStorage = (data: { content: FeatureJob[]; totalElements: number }) => {
    localStorage.setItem(this.getFeaturedJobsKey(), JSON.stringify(data));
  };

  private static storeArchivedJobsInLocalStorage = (data: { content: FeatureJob[]; totalElements: number }) => {
    localStorage.setItem(this.getArchivedJobsKey(), JSON.stringify(data));
  };

  private static storeJobApplicationsInLocalStorage = (data: { content: FeatureJobApplications[]; totalElements: number }) => {
    localStorage.setItem(this.getJobApplicationsKey(), JSON.stringify(data));
  };

  static getPreloadedFeaturedJobs(): { content: FeatureJob[]; totalElements: number } | null {
    const data = localStorage.getItem(this.getFeaturedJobsKey());
    return data ? JSON.parse(data) : null;
  }

  static getPreloadedArchivedJobs(): { content: FeatureJob[]; totalElements: number } | null {
    const data = localStorage.getItem(this.getArchivedJobsKey());
    return data ? JSON.parse(data) : null;
  }

  static getPreloadedJobApplications(): { content: FeatureJobApplications[]; totalElements: number } | null {
    const data = localStorage.getItem(this.getJobApplicationsKey());
    return data ? JSON.parse(data) : null;
  }

  static getPreloadedArchivedJobApplications(): { content: FeatureJobApplications[]; totalElements: number } | null {
    const data = localStorage.getItem('archivedJobApplications_preloaded');
    return data ? JSON.parse(data) : null;
  }

  static async preloadUserPhoto(userId: string, fileId?: string): Promise<void> {
    try {
      const localPhoto = this.getPhotoFromLocalStorage(userId);
      if (localPhoto) {
        return;
      }

      if (!fileId) {
        return;
      }

      const blob = await ProfileService.GetFile(userId, fileId);
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        this.storePhotoInLocalStorage(userId, dataUrl);
      };
      reader.readAsDataURL(blob);
    } catch (error: any) {
      console.warn('Failed to preload user photo:', error);
    }
  }

  static async preloadCompanyLogo(companyId: string): Promise<void> {
    try {
      const localLogo = this.getLogoFromLocalStorage(companyId);
      if (localLogo) {
        return;
      }

      const company = await CompanyService.Get(companyId);
      if (company?.companyFile?.id) {
        const blob = await CompanyService.GetFile(companyId);
        if (blob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            const dataUrl = reader.result as string;
            this.storeLogoInLocalStorage(companyId, dataUrl);
          };
          reader.readAsDataURL(blob);
          return;
        }
      }
    } catch (error: any) {
      console.warn('Failed to preload company logo:', error);
    }
  }

  static async preloadFeaturedJobs(): Promise<void> {
    try {
      const [activeResult, archivedResult] = await Promise.all([
        FeatureJobsService.Get({
          page: 1,
          size: 10,
          filter: 'deleted:eq:false',
        }),
        FeatureJobsService.Get({
          page: 1,
          size: 10,
          filter: 'deleted:eq:true',
        })
      ]);
      
      if (activeResult) {
        this.storeFeaturedJobsInLocalStorage(activeResult);
      }
      
      if (archivedResult) {
        this.storeArchivedJobsInLocalStorage(archivedResult);
      }
    } catch (error: any) {
      console.warn('Failed to preload featured jobs:', error);
    }
  }

  static async preloadJobApplications(): Promise<void> {
    try {
      const [activeResult, archivedResult] = await Promise.all([
        FeatureJobApplicationsService.GetApplications({
          page: 1,
          size: 10,
          filter: 'deleted:eq:false',
        }),
        FeatureJobApplicationsService.GetApplications({
          page: 1,
          size: 10,
          filter: 'deleted:eq:true',
        })
      ]);
      
      if (activeResult) {
        this.storeJobApplicationsInLocalStorage(activeResult);
      }
      
      if (archivedResult) {
        localStorage.setItem('archivedJobApplications_preloaded', JSON.stringify(archivedResult));
      }
    } catch (error: any) {
      console.warn('Failed to preload job applications:', error);
    }
  }

  static async preloadAssets(userId: string, companyId: string, fileId?: string): Promise<void> {
    await this.preloadUserPhoto(userId, fileId);
    await this.preloadCompanyLogo(companyId);
    await this.preloadFeaturedJobs();
    await this.preloadJobApplications();
  }

  static clearPreloadedData(): void {
    localStorage.removeItem(this.getFeaturedJobsKey());
    localStorage.removeItem(this.getArchivedJobsKey());
    localStorage.removeItem(this.getJobApplicationsKey());
    localStorage.removeItem('archivedJobApplications_preloaded');
  }
} 